import { StackTextGenerator } from "./CodeLocation";

type SerializationContext = string | object;

interface Errorish {
	readonly name: string;
	readonly message: string;
	readonly stack?: string;
	readonly cause?: unknown;
	readonly serializationContext?: SerializationContext | SerializationContext[];
}

export const isErrorish = (errorish: unknown): errorish is Errorish =>
	typeof errorish === "object" && errorish !== null &&
		"name" in errorish && "message" in errorish &&
		typeof errorish.name === "string" &&
		typeof errorish.message === "string" &&
		(!("stack" in errorish) || typeof errorish.stack === "string");

export class SerializableError implements Errorish {
	protected constructor(
		public readonly name: string,
		public readonly message: string,
		public readonly stack?: string,
		public readonly cause?: Errorish | unknown,
		public readonly serializationContext?: SerializationContext | SerializationContext[]
	) {
	}

	toString = () => {
		return JSON.stringify(this);
	}

	static fromError(e: Error | Errorish): SerializableError {
		return new SerializableError(e.name, e.message, e.stack, e.cause);
	}
	static fromSerializableError(serializableError: SerializableError): SerializableError {
		return new SerializableError(serializableError.name, serializableError.message, serializableError.stack, serializableError.cause);
	}
	static from(e: unknown, serializationContext?: SerializationContext): SerializableError {
		const prependSerializationContext = (context: SerializationContext | SerializationContext[] | undefined, newContext: SerializationContext | undefined): SerializationContext | SerializationContext[] | undefined => {
			if (context == null && newContext) return;
			if (context == null) return newContext;
			if (newContext == null) return context;
			const contextStack = [newContext, ...(Array.isArray(context) ? context : [context])];
			return contextStack;
		};

		if (isErrorish(e)) {
			return new SerializableError(e.name, e.message, e.stack, e.cause, prependSerializationContext(e.serializationContext, serializationContext));
		} else if (e == null) {
			return new SerializableError(
				`ErrorThrownAs${ e === null ? null : undefined}`,
				`Caught unknown error thrown as ${ e === null ? null : undefined}`,
				new StackTextGenerator({topFnToIgnore: SerializableError.from}).stackText,
				e,
				serializationContext
			);
		} else {
			let json: string | undefined;
			try {
				json = JSON.stringify(e);
			} catch { /* ignore errors */ }
			return new SerializableError(
				`ErrorThrownAs${typeof e}`,
				`Caught unknown error thrown as ${typeof e}: ${json}`,
				new StackTextGenerator({topFnToIgnore: SerializableError.from}).stackText,
				e,
				serializationContext
		);
		}
	}

	serialize = () => {
		const {name, message, stack, cause} = this;
		return {name, message, stack, cause};
	}

}
